/**
 * Uploads: store light (max-edge) images only — no full-resolution originals.
 * Same thumb path rules as scripts/image-thumbs/backfill.mjs.
 */

const MAX_EDGE_DEFAULT = 256
const MAX_EDGE_VENUE = 512
const SMALL_BYTES = 80_000
const SKIP_EXT = new Set(['svg', 'avif'])

export function thumbStoragePath(originalPath) {
  const clean = String(originalPath || '').replace(/^\/+/, '')
  if (clean.startsWith('thumbs/')) return clean
  return `thumbs/${clean}`.replace(/\.(jpe?g|png|webp|gif)$/i, (ext) => {
    return ext.toLowerCase() === '.png' ? '.png' : '.jpg'
  })
}

function extensionFromName(name, mimeType) {
  const fromName = String(name || '')
    .split('?')[0]
    .split('#')[0]
    .match(/\.([a-z0-9]{2,5})$/i)
  if (fromName?.[1]) return fromName[1].toLowerCase()
  const fromMime = String(mimeType || '').split('/')[1]
  if (fromMime) return fromMime.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'jpg'
  return 'jpg'
}

/**
 * @returns {Promise<{ skip: true, reason: string } | { skip: false, blob: Blob, contentType: string }>}
 */
export async function resizeImageFile(file, maxEdge = MAX_EDGE_DEFAULT) {
  const ext = extensionFromName(file.name, file.type)
  if (SKIP_EXT.has(ext)) return { skip: true, reason: ext }

  let bitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return { skip: true, reason: 'decode failed' }
  }

  const w = bitmap.width
  const h = bitmap.height
  const edge = Math.max(w, h)
  if (edge > 0 && edge <= maxEdge && file.size < SMALL_BYTES) {
    bitmap.close?.()
    return { skip: true, reason: 'already small' }
  }

  const scale = edge > 0 ? Math.min(1, maxEdge / edge) : 1
  const tw = Math.max(1, Math.round(w * scale))
  const th = Math.max(1, Math.round(h * scale))

  const canvas = document.createElement('canvas')
  canvas.width = tw
  canvas.height = th
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close?.()
    return { skip: true, reason: 'canvas unavailable' }
  }
  ctx.drawImage(bitmap, 0, 0, tw, th)
  bitmap.close?.()

  const usePng = ext === 'png' || file.type === 'image/png'
  const contentType = usePng ? 'image/png' : 'image/jpeg'
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not encode thumb'))),
      contentType,
      usePng ? undefined : 0.75,
    )
  })

  return { skip: false, blob, contentType }
}

async function lightBodyFromFile(file, maxEdge = MAX_EDGE_DEFAULT) {
  const resized = await resizeImageFile(file, maxEdge)
  if (resized.skip) {
    return { body: file, contentType: file.type || 'image/jpeg' }
  }
  return { body: resized.blob, contentType: resized.contentType }
}

async function uploadBodyToPath(supabase, bucket, path, body, contentType, { cacheControl, upsert }) {
  const { error } = await supabase.storage.from(bucket).upload(path, body, {
    cacheControl,
    upsert,
    contentType,
  })
  if (error) throw error
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  return data?.publicUrl || null
}

/**
 * Upload light image to path + thumbs/ copy. Returns public URLs.
 */
export async function uploadPublicImageWithThumb(
  supabase,
  {
    bucket,
    path,
    file,
    cacheControl = '3600',
    upsert = true,
    contentType: _contentType,
    maxEdge = MAX_EDGE_DEFAULT,
  },
) {
  const { body, contentType } = await lightBodyFromFile(file, maxEdge)

  const publicUrl = await uploadBodyToPath(supabase, bucket, path, body, contentType, {
    cacheControl,
    upsert,
  })
  if (!publicUrl) throw new Error('Could not resolve public URL for uploaded image.')

  let thumbPublicUrl = null
  const thumbPath = thumbStoragePath(path)
  try {
    thumbPublicUrl = await uploadBodyToPath(supabase, bucket, thumbPath, body, contentType, {
      cacheControl,
      upsert: true,
    })
  } catch (err) {
    console.warn('[imageThumbUpload] thumbs/ upload failed', err)
  }

  return { publicUrl, thumbPublicUrl, storagePath: path }
}

/**
 * Light upload from an in-memory Blob (e.g. match_photos canvas captures).
 */
export async function uploadLightBlob(
  supabase,
  bucket,
  path,
  blob,
  {
    maxEdge = MAX_EDGE_DEFAULT,
    upsert = true,
    cacheControl = '3600',
  } = {},
) {
  const name = path.split('/').pop() || 'photo.jpg'
  const file = new File([blob], name, { type: blob.type || 'image/jpeg' })
  const { body, contentType } = await lightBodyFromFile(file, maxEdge)

  const publicUrl = await uploadBodyToPath(supabase, bucket, path, body, contentType, {
    cacheControl,
    upsert,
  })

  const thumbPath = thumbStoragePath(path)
  try {
    await uploadBodyToPath(supabase, bucket, thumbPath, body, contentType, {
      cacheControl,
      upsert: true,
    })
  } catch (err) {
    console.warn('[imageThumbUpload] thumbs/ blob upload failed', err)
  }

  return publicUrl
}

export { MAX_EDGE_DEFAULT, MAX_EDGE_VENUE }
